'use client';

import type { ReactNode, SelectHTMLAttributes } from 'react';
import { forwardRef, useId } from 'react';
import { cx } from '../../utils/cx';
import styles from './Select.module.css';

export interface SelectOption {
  label: string;
  value: string;
}

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'size'
> {
  label?: string;
  description?: ReactNode;
  size?: 'S' | 'M';
  options: SelectOption[];
  className?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      description,
      size = 'M',
      options,
      className,
      id,
      disabled,
      ...rest
    },
    ref
  ) => {
    const reactId = useId();
    const fieldId = id ?? reactId;
    const descriptionId = description ? `${fieldId}-description` : undefined;

    return (
      <div
        className={cx(styles.root, className)}
        data-size={size}
        data-disabled={disabled || undefined}
      >
        {label && (
          <label className={styles.label} htmlFor={fieldId}>
            {label}
          </label>
        )}

        <div className={styles.selectWrap}>
          <select
            {...rest}
            ref={ref}
            id={fieldId}
            disabled={disabled}
            aria-describedby={descriptionId}
            className={styles.select}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className={styles.chevron} aria-hidden={true}>
            ▾
          </span>
        </div>

        {description && (
          <p className={styles.description} id={descriptionId}>
            {description}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
